import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import ReactDOM from "react-dom";
import type { ModalContextType, ShowModalOptions, ModalSize } from "./Modal.types";
import { Modal } from "./Modal";

const ANIMATION_DURATION = 200;

// --- Contexto ---
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// --- Provider ---
export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [content, setContent] = useState<ReactNode>(null);
  const [title, setTitle] = useState<string>("");
  const [size, setSize] = useState<ModalSize>("md");
  const [hideClose, setHideClose] = useState<boolean>(false);

  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const showModal = useCallback(
    ({ content, title, size = "md", hideCloseButton = false }: ShowModalOptions) => {
      setContent(content);
      setTitle(title);
      setSize(size);
      setHideClose(hideCloseButton);

      setIsMounted(true);

      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    },
    []
  );

  const hideModal = useCallback(() => {
    setIsVisible(false);

    setTimeout(() => {
      setIsMounted(false);
      setContent(null);
    }, ANIMATION_DURATION);
  }, []);

  const portalRoot = document.getElementById("modal-root") || document.body;

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}

      {isMounted &&
        ReactDOM.createPortal(
          <Modal
            isVisible={isVisible}
            onClose={hideModal}
            title={title}
            size={size}
            hideCloseButton={hideClose}
          >
            {content}
          </Modal>,
          portalRoot
        )}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
